import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { LoginPagina } from "@/pages/Login";
import { TwoFactorPagina } from "@/pages/TwoFactor";
import { WachtwoordVergetenPagina } from "@/pages/WachtwoordVergeten";
import { WachtwoordResetPagina } from "@/pages/WachtwoordReset";
import { RetourPagina } from "@/pages/Retour/RetourPagina";
import { InloglinkPagina } from "@/pages/Checkout/InloglinkPagina";
import { NaarVolgen, VolgenPagina } from "@/pages/Retour/VolgenPagina";
import { NotFoundPagina } from "@/pages/NotFound";
import { TevredenheidPagina } from "@/pages/Tevredenheid/TevredenheidPagina";
import { AfmeldenPagina } from "@/pages/Afmelden/AfmeldenPagina";
import { HerbestelPagina } from "@/pages/Herbestel/HerbestelPagina";
import { HerbestelBevestiging } from "@/pages/Herbestel/HerbestelBevestiging";

import { werkplekV6Routes } from "@/pages/WerkplekV6/routes";
import { beheerPadNaar } from "@/pages/WerkplekV6/Beheer/schermen";

import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { oudPadNaar } from "@/lib/oude-paden";

function NaarWerkplek() {
  const { pathname, search } = useLocation();
  // Sessie 70: oude beheerpaden (/admin/..., /gebruikers, /instellingen)
  // naar hetzelfde scherm onder /beheer; de rest zoals in sessie 67.
  const doel = beheerPadNaar(pathname, search) ?? oudPadNaar(pathname, search);
  return <Navigate to={doel} replace />;
}

/** Paden van het oude portaal die nergens meer heen gaan dan Vandaag. */
const OUDE_PADEN = [
  "/oud",
  "/oud/*",
  "/werkplek/*",
  "/inbox/*",
  "/email/*",
  "/emails/*",
  "/orders/*",
  "/gesprekken/*",
  "/commitments/*",
  "/meer/*",
  "/gebruikers/*",
  "/instellingen/*",
  "/admin/*",
  "/dashboard/*",
  "/reviews/concepten/*",
];

export default function App() {
  return (
    <>
      <Routes>
        {/* Publieke auth routes */}
        <Route path="/login" element={<LoginPagina />} />
        <Route path="/login/2fa" element={<TwoFactorPagina />} />
        <Route path="/wachtwoord-vergeten" element={<WachtwoordVergetenPagina />} />
        <Route path="/wachtwoord-reset" element={<WachtwoordResetPagina />} />

        {/* Publiek retourportaal (opdracht 20-07, achter RETOURPORTAAL_ENABLED
            op de API — de pagina zelf toont dan de gesloten-melding). */}
        <Route path="/retour" element={<RetourPagina />} />
        {/* Publieke volgpagina (sessie 134): bestelling of retour op
            order- of retournummer + e-mail, plus de tokenlink uit de
            retourmail. React-router matcht case-insensitief, dus
            /Volgen werkt. Het oude /RetourStatus (ook uit verstuurde
            mails) verwijst door met behoud van ?token=. */}
        <Route path="/volgen" element={<VolgenPagina />} />
        <Route path="/retourstatus" element={<NaarVolgen />} />
        {/* Publieke tevredenheidspagina (mailronde 06-08): tokenlink
            onder de handtekening van een verzonden antwoord. Achter
            MAIL_CSAT_ENABLED op de API — zonder vlag toont de pagina
            de ongeldig-melding. */}
        <Route path="/tevredenheid" element={<TevredenheidPagina />} />
        {/* Publieke afmeldpagina (recovery-ronde 20-08): tokenlink
            onderin de winkelwagen-herinnering. Afmelden gebeurt met
            een POST na een bevestigknop, nooit op de GET zelf. */}
        <Route path="/afmelden" element={<AfmeldenPagina />} />
        {/* Publieke herbestelpagina (herbestelronde 15-08): beveiligde
            bestellink (?token=) of shopklant-token (?shoptoken=).
            Achter de shop_extras-vlag "herbestelflow" op de API —
            zonder vlag toont de pagina de ongeldig-melding. */}
        <Route path="/herbestellen" element={<HerbestelPagina />} />
        {/* Bevestiging na de iDEAL-betaling (herbestel-afrekenen
            17-08): MSP stuurt de klant hierheen terug met alleen de
            eigen betaalreferentie (?ref=). */}
        <Route
          path="/herbestellen/bevestiging"
          element={<HerbestelBevestiging />}
        />
        {/* Foutpagina van de wachtwoordloze inloglink (knop-1b-sso
            30-08). De klant komt hier alleen als zijn link niet meer
            werkt; de landingsroute stuurt hem door met ?status=. Geen
            token, geen adres, geen klantnaam op deze pagina. */}
        <Route path="/inloglink" element={<InloglinkPagina />} />


        {/* Beschermde routes: werkplek v6 op / (sessie 67). */}
        <Route element={<ProtectedRoute />}>
          {werkplekV6Routes}
          {OUDE_PADEN.map((pad) => (
            <Route key={pad} path={pad} element={<NaarWerkplek />} />
          ))}
        </Route>

        <Route path="*" element={<NotFoundPagina />} />
      </Routes>

      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          duration: 4500,
        }}
      />
    </>
  );
}
